import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';

import { EmptyState } from '@/shared/ui/EmptyState';
import { BrandLogo } from '@/shared/ui/BrandLogo/BrandLogo';
import {
  assignLabelRows,
  computeMetricRow,
  groupHasData,
  markerLeft,
  METRIC_GROUPS,
  scaleFor,
  type MetricGroupId,
} from '../../model/extendedMetrics';
import { formatPercent, formatSigned } from '../../model/format';
import { useMetricLabels } from '../../model/metricLabels';
import type { YougovMonthlyRow } from '../../model/yougovData.schema';
import { AnalysisSelect, AnalysisSubHeader } from '../../../../components';

interface ExtendedMetricsProps {
  rows: YougovMonthlyRow[];
  brands: string[];
  targetBrand: string;
  /**
   * Export (print page) mode: the group selector can't be driven in a static PDF,
   * so all three groups (12 metrics) render stacked instead of just the selected one.
   */
  exporting?: boolean;
}

// A value pill is roughly 8% of the 720px minimum track width.
const MIN_LABEL_GAP_PERCENT = 8;
const LABEL_ROW_HEIGHT = 22;

const GROUP_LABEL_KEY: Record<MetricGroupId, 'awareness' | 'brandPerception' | 'ownership'> = {
  awareness: 'awareness',
  perception: 'brandPerception',
  ownership: 'ownership',
};

interface MetricTrackProps {
  rows: YougovMonthlyRow[];
  metric: string;
  brands: string[];
  targetBrand: string;
  label: string;
}

const MetricTrack = ({ rows, metric, brands, targetBrand, label }: MetricTrackProps) => {
  const { t } = useTranslation('analysis');
  const scale = scaleFor(metric);
  const formatValue = scale === 'percentage' ? formatPercent : formatSigned;
  const [activeBrand, setActiveBrand] = useState<string | null>(null);

  const markers = useMemo(
    () => assignLabelRows(computeMetricRow(rows, metric, brands), MIN_LABEL_GAP_PERCENT),
    [rows, metric, brands],
  );
  // Real data needs more than two label rows; size the container to whatever assignLabelRows opened.
  const rowCount = markers.reduce((max, m) => Math.max(max, m.labelRow + 1), 1);

  if (markers.length === 0) return null;

  const activate = (brand: string) => setActiveBrand(brand);
  // Guards against a mouseleave on one end clobbering state that the other end's
  // mouseenter already set for the same brand.
  const deactivate = (brand: string) =>
    setActiveBrand((current) => (current === brand ? null : current));

  const markersWithState = markers.map((marker) => ({
    ...marker,
    isActive: activeBrand === marker.brand,
    isDimmed: activeBrand !== null && activeBrand !== marker.brand,
  }));

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-3">
        <div className="text-sm font-normal text-foreground">{label}</div>
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="flex justify-between px-0.5 text-[11px] text-muted-foreground">
              {scale === 'percentage' ? (
                <>
                  <span>0%</span>
                  <span>100%</span>
                </>
              ) : (
                <>
                  <span>-100</span>
                  <span>0</span>
                  <span>100</span>
                </>
              )}
            </div>
            <div className="relative h-[42px]">
              <div className="absolute inset-x-0 top-1/2 h-4 -translate-y-1/2 rounded-full bg-linear-to-r from-purple-100 to-purple-400 dark:from-violet-800 dark:to-violet-600" />
              {markersWithState.map((marker) => {
                // The visible tooltip is brand-only — the value is already on screen (and
                // emphasised) on the pill. The accessible name still carries both, since a
                // screen-reader user can't see the emphasised pill.
                const markerAriaLabel = t('analysisResult.marketingPerformance.brandMetricValue', {
                  brand: marker.brand,
                  value: formatValue(marker.value),
                });
                return (
                  <Tooltip key={marker.brand} open={marker.isActive}>
                    <TooltipTrigger asChild>
                      {/* A native button gives keyboard focus and an accessible name for free.
                          It has no onClick — cursor-default overrides its default pointer cursor
                          so it doesn't promise a click action that isn't there. */}
                      <button
                        type="button"
                        aria-label={markerAriaLabel}
                        className={`absolute top-1/2 flex size-[34px] -translate-x-1/2 -translate-y-1/2 cursor-default items-center justify-center rounded-full bg-white shadow-[0_2px_5px_rgba(0,0,0,0.18)] dark:bg-card transition-opacity duration-fast ease-out select-none ${
                          marker.brand === targetBrand
                            ? 'border-[3px] border-green-500'
                            : 'border-2 border-neutral-300'
                        } ${marker.isActive ? 'z-[3] ring-2 ring-neutral-400' : 'z-[2]'} ${
                          marker.isDimmed ? 'opacity-40' : 'opacity-100'
                        }`}
                        style={{ left: markerLeft(marker.position) }}
                        onMouseEnter={() => activate(marker.brand)}
                        onMouseLeave={() => deactivate(marker.brand)}
                        onFocus={() => activate(marker.brand)}
                        onBlur={() => deactivate(marker.brand)}
                      >
                        <span className="size-[22px] overflow-hidden rounded-full">
                          <BrandLogo brandName={marker.brand} size={22} />
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{marker.brand}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
            <div className="relative" style={{ height: rowCount * LABEL_ROW_HEIGHT }}>
              {markersWithState.map((marker) => (
                <div
                  key={marker.brand}
                  className={`absolute -translate-x-1/2 cursor-default select-none whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium text-foreground-alt transition-opacity dark:bg-surface-raised dark:text-foreground duration-fast ease-out ${
                    marker.brand === targetBrand ? 'bg-surface' : 'bg-neutral-100'
                  } ${marker.isActive ? 'ring-2 ring-neutral-400' : ''} ${
                    marker.isDimmed ? 'opacity-40' : 'opacity-100'
                  }`}
                  style={{
                    left: markerLeft(marker.position),
                    top: marker.labelRow * LABEL_ROW_HEIGHT,
                  }}
                  onMouseEnter={() => activate(marker.brand)}
                  onMouseLeave={() => deactivate(marker.brand)}
                >
                  {formatValue(marker.value)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

interface MetricGroupCardProps {
  groupId: MetricGroupId;
  rows: YougovMonthlyRow[];
  brands: string[];
  targetBrand: string;
  metricLabels: Record<string, string>;
  /** Rendered above the card. Only set in export mode, where the group selector is gone. */
  heading?: string;
}

const MetricGroupCard = ({
  groupId,
  rows,
  brands,
  targetBrand,
  metricLabels,
  heading,
}: MetricGroupCardProps) => {
  const { t } = useTranslation('analysis');
  const metrics = METRIC_GROUPS[groupId];
  const hasData = groupHasData(rows, metrics, brands);

  return (
    <div className="flex flex-col gap-3">
      {heading && <div className="text-sm font-medium text-foreground">{heading}</div>}
      <div className="flex flex-col gap-7 rounded-xl border border-border-light bg-card p-6">
        {hasData ? (
          metrics.map((metric) => (
            <MetricTrack
              key={metric}
              rows={rows}
              metric={metric}
              brands={brands}
              targetBrand={targetBrand}
              label={metricLabels[metric]}
            />
          ))
        ) : (
          <EmptyState
            title={t('analysisResult.marketingPerformance.noDataForMetrics')}
            description={t('analysisResult.marketingPerformance.noDataForMetricsDescription')}
          />
        )}
      </div>
    </div>
  );
};

export const ExtendedMetrics = ({ rows, brands, targetBrand, exporting }: ExtendedMetricsProps) => {
  const { t } = useTranslation('analysis');
  const [group, setGroup] = useState<MetricGroupId>('awareness');
  const metricLabels = useMetricLabels();
  const groupIds = Object.keys(METRIC_GROUPS) as MetricGroupId[];
  const groupOptions = groupIds.map((groupId) => ({
    value: groupId,
    label: t(`analysisResult.marketingPerformance.${GROUP_LABEL_KEY[groupId]}`),
  }));

  return (
    <div className="flex flex-col gap-4">
      <AnalysisSubHeader
        title={t('analysisResult.marketingPerformance.extendedMetrics')}
        subtitle={t('analysisResult.marketingPerformance.extendedMetricsSubtitle')}
        subtitleTone="meta"
      />
      {!exporting && (
        <div className="flex items-center gap-2.5">
          <AnalysisSelect
            variant="section"
            ariaLabel={t('analysisResult.marketingPerformance.metricGroup')}
            value={group}
            onValueChange={(value) => setGroup(value as MetricGroupId)}
            options={groupOptions}
          />
        </div>
      )}
      {exporting ? (
        groupIds.map((groupId) => (
          <MetricGroupCard
            key={groupId}
            groupId={groupId}
            rows={rows}
            brands={brands}
            targetBrand={targetBrand}
            metricLabels={metricLabels}
            heading={t(`analysisResult.marketingPerformance.${GROUP_LABEL_KEY[groupId]}`)}
          />
        ))
      ) : (
        <MetricGroupCard
          groupId={group}
          rows={rows}
          brands={brands}
          targetBrand={targetBrand}
          metricLabels={metricLabels}
        />
      )}
    </div>
  );
};
